/**
 * ============================================================================
 * The user's date/time display preferences, readable from a PURE function
 * ============================================================================
 *
 * `formatDate.ts` is a plain module, not a hook, and it is called from ~40 places. To make the My
 * Profile preferences actually take effect, the formatters need to read the current choice — but they
 * cannot call `useUiPrefsStore()`.
 *
 * So the preference lives in a module-level holder that the store WRITES on change and the formatters
 * READ. Zustand's `subscribe` keeps it in step, wired once in `providers.tsx`.
 *
 * Only the TIME notation still travels that way. The date order is a constant — see `DATE_FORMAT`.
 *
 * Why not thread a parameter through every call site: forty signatures would change, every caller
 * would need the hook, and a missed one would silently keep the old format — the same class of
 * inconsistency the port has been removing elsewhere.
 *
 * ⚠️ This is the INTERIM behaviour for Q1. `saveProfile()` in Angular is a TODO no-op and there is no
 * persistence endpoint, so the choice is stored per-browser in `localStorage` via `uiPrefs`. When an
 * endpoint exists, only the store's hydration source changes; nothing here moves.
 */

/**
 * ============================================================================
 * ⚠️ THE DATE FORMAT IS FIXED AT DD-MM-YYYY. IT USED TO BE A USER CHOICE.
 * ============================================================================
 *
 * `DateFormatPref` offered `dd/MM/yyyy | MM/dd/yyyy | yyyy-MM-dd` and My Profile had a picker for
 * it. The product removed the choice: every date in the app reads day-first, and there is no
 * setting to change it.
 *
 * Making it a constant rather than a one-option preference is the point — a preference with one
 * value is still a preference, and the next person to add a second value would reopen a decision
 * that has been made. The TIME notation stays a choice; only the date order was fixed.
 *
 * ⚠️ THE SEPARATOR IS A HYPHEN, NOT A SLASH, AND THIS ONE LINE IS WHY EVERY DATE AGREES. The
 * product asked for DD-MM-YYYY on the Business Partner form and its printed report; changing it here
 * rather than at those two screens is what keeps the grids, the detail pages, the dialogs and the
 * report reading the same way. A date that renders 20-09-2026 on the form and 20/09/2026 in the list
 * behind it looks like two systems.
 *
 * Two places still have to be kept in step by hand, because neither goes through `date-fns`: the
 * text date control's own converter (`utils/dateInput.ts`, which also owns what the user may TYPE)
 * and the printed report (`vendorPrint.printValue`, which formats straight off the API's ISO
 * strings). Both carry a note pointing back here.
 *
 * ⚠️ A BROWSER THAT ALREADY CHOSE `MM/dd/yyyy` WILL SWITCH ON THE NEXT LOAD. The stored key is
 * simply no longer read — see `onRehydrateStorage` — which is the intended outcome, not a
 * migration gap.
 */
export const DATE_FORMAT = 'dd-MM-yyyy';

/** 12-hour with am/pm, or 24-hour. Angular used 12-hour throughout. */
export type TimeNotationPref = '12h' | '24h';

export interface DateDisplayPrefs {
  timeNotation: TimeNotationPref;
}

/** The one display preference that is still a choice. Angular used 12-hour throughout. */
export const DEFAULT_DATE_PREFS: DateDisplayPrefs = {
  timeNotation: '12h',
};

let current: DateDisplayPrefs = { ...DEFAULT_DATE_PREFS };

/** Called by the store subscription. Not for component use. */
export function setDateDisplayPrefs(prefs: DateDisplayPrefs): void {
  current = prefs;
}

export function getDateDisplayPrefs(): DateDisplayPrefs {
  return current;
}

/** The date-fns pattern for a date alone. Constant — see `DATE_FORMAT`. */
export function datePattern(): string {
  return DATE_FORMAT;
}

/** The date-fns pattern for date + time, honouring both preferences. */
export function dateTimePattern(): string {
  // `h:mm a` → 12-hour with am/pm; `HH:mm` → 24-hour, zero-padded.
  return `${DATE_FORMAT} ${current.timeNotation === '24h' ? 'HH:mm' : 'h:mm a'}`;
}
